/*
 * Copyright (c) Nexign, JSC, 2019-2022.
 */
/**
 *
 * @author Yakov Gushchin
 * @author Evgenij Tyan
 * @author Anton Panasenko
 * @author Aleksandr Perkhunov
 * @version 1.16.0
 * @since 1.0.0
 */

import styled from 'styled-components';

import { icons } from '../../../../assets';
import { checkbox } from '../../../../theme';
import { IStyledProps } from '../Checkbox';

const CheckBoxContainerStyled = styled.div.attrs(({ dataName }: { dataName: string }) => ({
    'tabIndex': 0,
    'data-name': dataName || 'checkbox',
}))<IStyledProps>`
    display: flex;
    align-items: center;
    position: relative;
    min-width: 20px;
    height: 20px;
    outline: none;
    > input {
        opacity: 0;
        position: absolute;
    }
    > label {
        padding-left: 32px;
    }
    > input + label {
        cursor: pointer;
        box-sizing: border-box;
        &:before { /* Border and background color*/
            content: '';
            position: absolute;
            left:0;
            top: 0;
            width: 20px;
            height: 20px;
            border-radius: 2px;
            border: solid 2px ${checkbox.borderColor};
            background-color: ${checkbox.bgColor};
            box-sizing: border-box;
        }
        &:after { /* Checked state */
            content: ' ';
            position: absolute;
            font-size: 16px;
            top: 0;
            left: 0;
            transition: all .2s;
            background-image: url(${icons.checkedIcon});
            width: 20px;
            height: 20px;
        }
    }
    > input:not(:checked) + label {
        &:after { /* Checked icon don't display */
            opacity: 0;
            transform: scale(0);
        }
    }
    &:focus > input:not(:checked) + label,
    > input:not(:checked) + label:hover,
    &.hover > input:not(:checked) + label {
        &:before { /* Border and background color for hover state */
            box-shadow: none;
            border-color: ${props => props.colorChecked ?? checkbox.borderColorHover};
        }
    }
    > input:not(:checked) + label:active {
        &:before { /* Border and background color for hover state */
            box-shadow: none;
            border-color: ${props => props.colorChecked ?? checkbox.borderColorDown};
        }
    }
    > input:disabled:not(:checked) + label {
        cursor: default;
        &:before { /* Border and background color for disable state */
            box-shadow: none;
            border-color: ${checkbox.borderColor};
            background-color: ${checkbox.bgColorDisable};
            cursor: default;
        }
    }
    > input:checked + label {
        &:after {  /* Checked icon display */
            opacity: 1;
            transform: scale(1);
        }
        &:before { /* Border and background color for checked state */
            content: '';
            position: absolute;
            left:0; top: 0;
            width: 20px;
            height: 20px;
            border-radius: 2px;
            border: solid 2px ${props => props.colorChecked ?? checkbox.borderColorChecked};
            background-color: ${props => props.colorChecked ?? checkbox.bgColorChecked};
        }
    }
    &:focus > input:checked + label,
    > input:checked + label:hover,
    &.hover > input:checked + label {
        &:before {    /* Border and background color for checked and hover state */
            box-shadow: none;
            border-color: ${props => props.colorChecked ?? checkbox.borderColorCheckedHover};
            background-color: ${props => props.colorChecked ?? checkbox.bgColorCheckedHover};
        }
    }
    > input:checked + label:active {
        &:before {    /* Border and background color for checked and disabled state */
            box-shadow: none;
            border-color: ${props => props.colorChecked ?? checkbox.borderColorDown};
            background-color: ${props => props.colorChecked ?? checkbox.bgColorCheckedDown};
        }
    }
    > input:disabled:checked + label {
        cursor: default;
        &:before {    /* Border and background color for checked and disabled state */
            box-shadow: none;
            border-color: ${props => props.colorBorderDisabled ?? checkbox.borderColor};
            background-color: ${props => props.colorDisabled ?? checkbox.bgColorCheckedDisable};
            cursor: default;
        }
    }

    &.isNotChangeable input + label {
        cursor: default;
    }
`;

export {
    CheckBoxContainerStyled,
};
